import s from './RegisterForm.module.scss'
import React, { useCallback, useContext, useState } from 'react'
import { Button, Collapse } from 'antd'
import {
    DownloadOutlined,
    SafetyCertificateOutlined,
    WalletOutlined,
} from '@ant-design/icons'
import { PopupAdapterFormProps } from 'shared/components/popups/PopupAdapter.types'
import Logo from 'shared/img/logo.svg'
import { CheckboxControl } from 'shared/components/CheckboxControl'
import {
    WhyTronLink,
    whyTronLinkContent,
} from 'shared/components/WhyTronLink'
import { Link } from 'react-router-dom'
import { NAV_LINK_NAMES } from 'shared/consts'
import cn from 'classnames'
import { AccountContext } from 'core/context'
import translations from './translations'
import { useLanguage } from 'context/LanguageContext'

const { Panel } = Collapse

export const RegisterForm: React.FC<PopupAdapterFormProps> = React.memo(
    ({ onRequestFinish, id }) => {
        const { tronLinkRegister } = useContext(AccountContext)
        const [isTerms, setIsTerms] = useState(false)

        const { language } = useLanguage()
        const t = translations[language]
        const why = whyTronLinkContent[language] ?? whyTronLinkContent['en']

        const handleRegister = useCallback(async () => {
            if (!isTerms) return
            const ok = await tronLinkRegister()
            if (ok) onRequestFinish?.()
        }, [isTerms, tronLinkRegister, onRequestFinish])

        return (
            <div id={id} className={s.wrapper}>
                <img src={Logo} alt="RealColibri" className={s.logo} />

                <h2 className={s.title}>{t.CREATE_ACCOUNT}</h2>
                <p className={s.subtitle}>{t.SUBTITLE}</p>

                <div className={s.steps}>
                    <div className={s.step}>
                        <span className={s.badge}>1</span>
                        <div className={s.stepBody}>
                            <span className={s.stepTitle}>
                                {t.STEP_INSTALL_TITLE}
                            </span>
                            <span className={s.stepText}>
                                {t.STEP_INSTALL_TEXT}
                            </span>
                            <Button
                                href="https://www.tronlink.org/"
                                target="_blank"
                                rel="noopener noreferrer"
                                icon={<DownloadOutlined />}
                                className={s.installBtn}
                            >
                                {t.INSTALL_TRONLINK}
                            </Button>
                        </div>
                    </div>

                    <div className={s.step}>
                        <span className={s.badge}>2</span>
                        <div className={s.stepBody}>
                            <span className={s.stepTitle}>
                                {t.STEP_REGISTER_TITLE}
                            </span>
                            <span className={s.stepText}>
                                {t.STEP_REGISTER_TEXT}
                            </span>
                        </div>
                    </div>
                </div>

                <div className={s.secureNote}>
                    <SafetyCertificateOutlined className={s.secureIcon} />
                    <span>{t.SECURE_NOTE}</span>
                </div>

                <Collapse ghost className={s.why}>
                    <Panel
                        key="why"
                        header={
                            <span className={s.whyHeader}>
                                <SafetyCertificateOutlined />
                                {why.title}
                            </span>
                        }
                    >
                        <WhyTronLink hideTitle />
                    </Panel>
                </Collapse>

                <CheckboxControl
                    className={cn(s.terms, !isTerms && s.termsError)}
                    checked={isTerms}
                    onChange={e => setIsTerms(e.target.checked)}
                >
                    <span>
                        {t.TERMS_AGREE}{' '}
                        <Link
                            to={NAV_LINK_NAMES.TERMS}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {t.TERMS_CONDITIONS}
                        </Link>
                    </span>
                </CheckboxControl>

                <Button
                    type="primary"
                    size="large"
                    block
                    icon={<WalletOutlined />}
                    className={s.submit}
                    disabled={!isTerms}
                    onClick={handleRegister}
                >
                    {t.REGISTER_WITH_TRONLINK}
                </Button>
            </div>
        )
    }
)
